import { FlowState } from '@de/providers/dynamicExperienceReducer';
import {
  DynamicOptions,
  Step,
  ResultsConfig,
  PaginationData,
} from '@de/types/types';
import {
  fetchWithDynamicParams,
  JsonData,
  extractJsonPathValues,
  SortContext,
} from './dynamicFetchUtils';
import { SearchParamsOptions } from '@de/utils/types';

export interface CustomResultsWithPagination {
  results: ResultsConfig[];
  pagination?: PaginationData;
}

const extractPagination = (data: JsonData): PaginationData | undefined => {
  if (typeof data !== 'object' || data === null || Array.isArray(data))
    return undefined;
  const pagination = (data as Record<string, unknown>).pagination;
  if (
    typeof pagination !== 'object' ||
    pagination === null ||
    Array.isArray(pagination)
  )
    return undefined;
  const p = pagination as Record<string, unknown>;
  if (typeof p.total !== 'number' || typeof p.limit !== 'number')
    return undefined;
  return {
    total: p.total,
    limit: p.limit,
    offset: typeof p.offset === 'number' ? p.offset : 0,
    ...(typeof p.totalCount === 'number' ? { totalCount: p.totalCount } : {}),
  };
};

const mapResponseToCustomResults = (
  data: JsonData,
  dynamicOptions: DynamicOptions,
): CustomResultsWithPagination => {
  const { limit, resultsConfig } = dynamicOptions;

  if (!resultsConfig) {
    throw new Error('Results config is required for custom results');
  }

  const {
    title: titlePath,
    subtitle: subtitlePath,
    image: imagePath,
    logo: logoPath,
    ctaText: ctaTextPath,
    redirectUrl: redirectUrlPath,
    stats: statsConfig,
    subHeading: subHeadingPath,
    heading: headingPath,
    compareConfig: compareConfig,
  } = resultsConfig;

  // Extract data using JSONPath expressions
  const titles = extractJsonPathValues(titlePath, data);
  const subtitles = extractJsonPathValues(subtitlePath, data);
  const images = extractJsonPathValues(imagePath, data);
  const logos = extractJsonPathValues(logoPath, data);
  const ctaTexts = extractJsonPathValues(ctaTextPath, data);
  const redirectUrls = extractJsonPathValues(redirectUrlPath, data);
  const subHeadings = extractJsonPathValues(subHeadingPath, data);
  const heading = extractJsonPathValues(headingPath, data);

  // Extract stats values using JSONPath for each stat config item
  // statsConfig is an array of {icon, text} where text is a JSONPath
  const statsArrays = statsConfig
    ? statsConfig.map((statItem) => ({
        icon: statItem.icon,
        values: extractJsonPathValues(statItem.text, data),
      }))
    : [];

  // Extract compare attribute values per result item
  const compareAttributeArrays =
    compareConfig?.compareAttributes?.map((attr) => ({
      label: attr.label ?? '',
      key: attr.key ?? '',
      values: extractJsonPathValues(attr.key, data),
    })) ?? [];

  // Determine the length based on the shortest array to avoid index out of bounds
  // Filter out empty arrays (optional fields) before calculating min

  const statValueArrays = statsArrays.map((s) => s.values);
  const nonEmptyArrays = [
    titles,
    subtitles,
    images,
    logos,
    ctaTexts,
    redirectUrls,
    subHeadings,
    ...statValueArrays,
  ].filter((arr) => arr.length > 0);

  const length =
    nonEmptyArrays.length > 0
      ? Math.min(...nonEmptyArrays.map((arr) => arr.length))
      : 0;

  // Apply limit if specified
  const finalLength = limit ? Math.min(length, limit) : length;

  const results = Array.from({ length: finalLength }, (_, index) => ({
    title: String(titles[index] || ''),
    subtitle: String(subtitles[index] || ''),
    image: String(images[index] || ''),
    logo: String(logos[index] || ''),
    ctaText: String(ctaTexts[index] || ''),
    redirectUrl: String(redirectUrls[index] || ''),
    stats: statsArrays.map((stat) => ({
      icon: stat.icon,
      text: String(stat.values[index] || ''),
    })),
    subHeading: String(subHeadings[index] || ''),
    heading: String(heading[index] || ''),
    compareConfig: resultsConfig.compareConfig,
    compareAttributeValues: compareAttributeArrays.map((attr) => ({
      label: attr.label,
      key: attr.key,
      value: String(attr.values[index] ?? '-'),
    })),
  })) as ResultsConfig[];

  return { results, pagination: extractPagination(data) };
};

export const fetchCustomResults = async (
  dynamicContent: DynamicOptions,
  flowState: FlowState,
  steps: Step[],
  searchParamsOptions?: SearchParamsOptions,
  sortContext?: SortContext,
): Promise<CustomResultsWithPagination> => {
  return fetchWithDynamicParams(
    { dynamicContent, flowState, steps, searchParamsOptions, sortContext },
    'custom results',
    mapResponseToCustomResults,
  );
};
